import { ContextDisplayNames } from "@/Chat/ChatSidebar/hooks/useRobotoLLMContext";
import { RobotoLLMContext } from "@/shared/domain/chat/ChatRecord";
import { pluralize } from "@/shared/text";

import { type ContextType } from "../contextType";

import { ContextGroup, ContextItem } from "./ContextGroup";
import styles from "./ContextWidget.module.css";

interface ContextWidgetProps {
  context: RobotoLLMContext;
  displayNames: ContextDisplayNames;
  onDelete: (contextType: ContextType, contextContent: string) => void;
}

/**
 * Widget that displays the current RobotoLLMContext grouped by type.
 */
export const ContextWidget = (props: ContextWidgetProps) => {
  // Don't render if there's no context
  const hasContext =
    props.context.dataset_ids.length > 0 ||
    props.context.file_ids.length > 0 ||
    props.context.visualizer_state;

  if (!hasContext) {
    return null;
  }

  // Transform LLM context to displayable context groups
  const contextGroups = toContextGroups(props.context, props.displayNames);

  return (
    <div className={styles.contextContainer}>
      {/* Visualizer group */}
      <ContextGroup
        contextType="viz_state"
        items={contextGroups.visualizer}
        onDelete={() => props.onDelete("viz_state", "")}
      />

      {/* Dataset group */}
      <ContextGroup
        contextType="dataset"
        items={contextGroups.datasets}
        onDelete={(id) => props.onDelete("dataset", id)}
      />

      {/* Files group */}
      <ContextGroup
        contextType="file"
        items={contextGroups.files}
        onDelete={(id) => props.onDelete("file", id)}
      />
    </div>
  );
};

interface ContextGroups {
  datasets: ContextItem[];
  files: ContextItem[];
  visualizer: ContextItem[];
}

const MAX_FILE_PATH_LENGTH = 20;

/**
 * Transforms RobotoLLMContext into context items grouped by type.
 */
function toContextGroups(
  context: RobotoLLMContext,
  displayNames: ContextDisplayNames,
): ContextGroups {
  // Prepare dataset items
  const datasets = context.dataset_ids.map((datasetId) => ({
    id: datasetId,
    label: datasetId,
    tooltip: displayNames.datasets[datasetId],
  }));

  // Prepare file items
  const files = context.file_ids.map((fileId) => {
    const filePath = displayNames.files[fileId] ?? fileId;
    const needsTruncation = filePath.length > MAX_FILE_PATH_LENGTH;
    return {
      id: fileId,
      label: truncateMiddle(filePath, MAX_FILE_PATH_LENGTH),
      tooltip: needsTruncation ? filePath : undefined,
    };
  });

  // Prepare visualizer items
  const visualizer: ContextItem[] = [];
  if (context.visualizer_state) {
    // Extract panel count from visualizer state
    const numPanels =
      typeof context.visualizer_state === "object" &&
      "panels" in context.visualizer_state &&
      typeof context.visualizer_state.panels === "object" &&
      context.visualizer_state.panels !== null
        ? Object.keys(context.visualizer_state.panels).length
        : 0;

    visualizer.push({
      id: "viz_state",
      label:
        numPanels === 0
          ? "empty_visualizer"
          : pluralize(`${numPanels}_viz_panel`, numPanels),
      tooltip: undefined,
    });
  }

  return { datasets, files, visualizer };
}

/**
 * Truncates a file path in the middle if it exceeds the max length.
 * e.g., "path/to/some/long/file.bag" -> "path/to...file.bag"
 */
function truncateMiddle(str: string, maxLength: number): string {
  if (str.length <= maxLength) {
    return str;
  }
  const ellipsis = "...";
  const charsToShow = maxLength - ellipsis.length;
  const frontChars = Math.ceil(charsToShow / 2);
  const backChars = Math.floor(charsToShow / 2);
  return str.slice(0, frontChars) + ellipsis + str.slice(-backChars);
}
